import { bus } from '../core/bus';
import { S } from '../core/game';
import { m } from '../i18n';
import { sayEnglish } from '../audio/speech';
import { sfx } from '../audio/sfx';
import { progress, recentDays, wordReport, LEVELS } from '../content/passport';
import { SCENARIOS, type ScenarioId } from '../content/vocab';
import { h } from './dom';
import { closePanel, isPanelOpen, openPanel, refreshPanel } from './panel';
import { openEnglish } from './english';

/** Stamps for places and festivals, in the order they appear in the passport. */
export const PLACE_STAMPS: readonly { id: keyof ReturnType<typeof m>['passport']['stampNames']; icon: string; color: string }[] = [
  { id: 'nightMarket', icon: '🏮', color: '#d0453a' },
  { id: 'lantern', icon: '🪔', color: '#e0a020' },
  { id: 'dragonBoat', icon: '🐉', color: '#2e9d6a' },
  { id: 'moon', icon: '🥮', color: '#c27a2a' },
  { id: 'newYear', icon: '🧧', color: '#c0392b' },
  { id: 'taipei101', icon: '🏙️', color: '#3a6fb0' },
  { id: 'mrt', icon: '🚇', color: '#1f8a9a' },
  { id: 'museum', icon: '🏺', color: '#7a4ab0' },
  { id: 'taroko', icon: '⛰️', color: '#4a7a5a' },
  { id: 'beach', icon: '🌊', color: '#2a7ac0' },
  { id: 'jiufen', icon: '🍵', color: '#a0522d' },
  { id: 'xiangshan', icon: '🐘', color: '#3d7a46' },
  { id: 'ximen', icon: '🛍️', color: '#c0306a' },
  { id: 'longshan', icon: '🏮', color: '#b8322a' },
  { id: 'cks', icon: '🏯', color: '#2a5aa8' },
  { id: 'beitou', icon: '♨️', color: '#5a8a6a' },
  { id: 'dongdamen', icon: '🍢', color: '#d0602a' },
  { id: 'jian', icon: '☕', color: '#2f7a7a' },
];

let parentsOpen = false;

/** A tilted ink stamp; the tilt is stable per id so stamps don't wobble on refresh. */
function inkStamp(id: string, icon: string, label: string, color: string, got: boolean, stars = 0): HTMLElement {
  let hsh = 0;
  for (const ch of id) hsh = (hsh * 31 + ch.charCodeAt(0)) | 0;
  const tilt = ((hsh % 17) - 8) * 1.2;
  return h(
    'div',
    { class: `stamp${got ? ' got' : ''}`, style: got ? `--ink:${color}; transform: rotate(${tilt}deg)` : '' },
    h('span', { class: 'stamp-ico' }, icon),
    h('small', null, got ? label : m().passport.notYet),
    stars ? h('i', null, '★'.repeat(stars) + '☆'.repeat(3 - stars)) : null,
  );
}

export function openPassport(): void {
  openPanel('passport', () => {
    const t = m().passport;
    const s = S();
    const p = progress();
    const days = recentDays(6);
    const peak = Math.max(4, ...days.map((d) => d.words + d.talks + d.spells));
    const report = wordReport();
    const nextAt = LEVELS[p.level + 1];
    const stat = (n: number, label: string) => h('div', { class: 'pp-stat' }, h('b', null, String(n)), h('small', null, label));
    return {
      title: t.title,
      className: 'passport',
      body: [
        h(
          'div',
          { class: 'pp-cover' },
          h('div', { class: 'pp-emblem' }, '🌸'),
          h(
            'div',
            { class: 'pp-id' },
            h('small', null, 'PASSPORT · 護照'),
            h('b', null, t.levels[p.level]),
            h('div', { class: 'pp-bar' }, h('i', { style: `width:${Math.round(p.toNext * 100)}%` })),
            h('small', null, nextAt === undefined ? t.top : `${t.xp(p.xp)} · ${t.next(nextAt - p.xp)}`),
          ),
          h('div', { class: 'pp-level' }, `Lv ${p.level + 1}`),
        ),
        h(
          'div',
          { class: 'pp-stats' },
          stat(p.known, t.stats.known),
          stat(p.mastered, t.stats.mastered),
          stat(p.spelled, t.stats.spelled),
          stat(p.shadowed, t.stats.shadowed),
          stat(p.talks, t.stats.talks),
          stat(p.stories, t.stats.stories),
        ),
        h('h4', null, `📊 ${t.week}`),
        h(
          'div',
          { class: 'pp-chart' },
          days.map((d) =>
            h(
              'div',
              { class: 'pp-col' },
              h(
                'div',
                { class: 'pp-stack' },
                h('i', { class: 'w', style: `height:${(d.words / peak) * 100}%` }),
                h('i', { class: 't', style: `height:${(d.talks / peak) * 100}%` }),
                h('i', { class: 's', style: `height:${(d.spells / peak) * 100}%` }),
              ),
              h('small', null, d.day >= 3 ? t.day(d.day - 2) : '·'),
            ),
          ),
        ),
        h(
          'div',
          { class: 'pp-legend' },
          h('span', { class: 'w' }, t.legend.words),
          h('span', { class: 't' }, t.legend.talks),
          h('span', { class: 's' }, t.legend.spells),
        ),
        h('h4', null, `🗣️ ${t.talkStamps}`),
        h(
          'div',
          { class: 'stamps' },
          (Object.keys(SCENARIOS) as ScenarioId[]).map((id) => {
            const stars = s.english.talks[id] ?? 0;
            const name = (m().english2.scenarios as Record<string, string>)[id] ?? (m().places.scenarios as Record<string, string>)[id] ?? id;
            return inkStamp(id, SCENARIOS[id].icon, name, '#2f6fb0', stars > 0, stars);
          }),
        ),
        h('h4', null, `🗺️ ${t.placeStamps}`),
        h(
          'div',
          { class: 'stamps' },
          PLACE_STAMPS.map((st) => inkStamp(st.id, st.icon, t.stampNames[st.id], st.color, s.english.stamps[st.id] !== undefined)),
        ),
        h(
          'button',
          { class: `pp-parents-toggle${parentsOpen ? ' open' : ''}`, onclick: () => ((parentsOpen = !parentsOpen), sfx('click'), refreshPanel('passport')) },
          t.parents,
        ),
        parentsOpen
          ? h(
              'div',
              { class: 'pp-parents' },
              h('p', { class: 'hint' }, t.parentsHint),
              h('b', null, `💪 ${t.strong} (${report.strong.length})`),
              h('div', { class: 'chips' }, report.strong.length ? report.strong.map((w) => h('button', { class: 'chip small', onclick: () => sayEnglish(w, 0.8) }, w)) : h('small', null, t.none)),
              h('b', null, `🔁 ${t.weak} (${report.weak.length})`),
              h('div', { class: 'chips' }, report.weak.length ? report.weak.map((w) => h('button', { class: 'chip small', onclick: () => sayEnglish(w, 0.8) }, w)) : h('small', null, t.none)),
            )
          : null,
      ],
      footer: [h('button', { class: 'btn gold', onclick: () => (closePanel('passport'), openEnglish()) }, t.practise)],
    };
  });
}

bus.on('change', () => {
  if (isPanelOpen('passport')) refreshPanel('passport');
});
